import { useState, useEffect } from "react";
import { History, Clock, User, Activity, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { getRecentAudit } from "@/lib/api";

const ACTION_LABELS = {
  login_success: { label: "Inicio de Sesión", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  status_changed: { label: "Cambio de Semáforo", color: "bg-blue-50 text-blue-700 border-blue-200" },
  payment_added: { label: "Abono Registrado", color: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  payment_deleted: { label: "Abono Eliminado", color: "bg-red-50 text-red-700 border-red-200" },
  doc_uploaded: { label: "Documento Subido", color: "bg-sky-50 text-sky-700 border-sky-200" },
  doc_deleted: { label: "Documento Eliminado", color: "bg-amber-50 text-amber-700 border-amber-200" },
  email_sent: { label: "Correo Enviado", color: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  client_created: { label: "Cliente Creado", color: "bg-slate-100 text-slate-800 border-slate-300" },
  client_updated: { label: "Cliente Modificado", color: "bg-slate-100 text-slate-700 border-slate-200" },
  user_created: { label: "Usuario Creado", color: "bg-purple-50 text-purple-700 border-purple-200" },
};

export const AuditDialog = ({ open, onOpenChange }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setLoading(true);
      getRecentAudit(60)
        .then((data) => setLogs(data))
        .catch(() => {})
        .finally(() => setLoading(false));
    }
  }, [open]);

  const formatLogDate = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleString("es-ES", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100 bg-slate-50/50">
          <div className="flex items-center gap-2 text-slate-800">
            <History className="h-5 w-5 text-slate-600" />
            <DialogTitle className="font-heading text-lg">
              Registro de Auditoría y Actividad
            </DialogTitle>
          </div>
          <DialogDescription className="text-xs text-slate-500">
            Trazabilidad de modificaciones, abonos, cambios de estatus y envíos en el sistema.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {loading ? (
            <div className="py-12 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Cargando bitácora de actividad...
            </div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">
              No hay registros de actividad recientes.
            </p>
          ) : (
            <div className="relative border-l border-slate-200 ml-3 space-y-4 pl-4">
              {logs.map((item) => {
                const meta = ACTION_LABELS[item.action] || {
                  label: item.action,
                  color: "bg-slate-100 text-slate-700 border-slate-200",
                };
                return (
                  <div key={item.id} className="relative group">
                    <span className="absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full bg-slate-400 border-2 border-white ring-1 ring-slate-200" />
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${meta.color}`}
                        >
                          {meta.label}
                        </span>
                        <span className="text-xs font-medium text-slate-600">
                          @{item.username}
                        </span>
                      </div>
                      <span className="text-[11px] font-mono text-slate-400 flex items-center gap-1">
                        <Clock className="h-3 w-3" /> {formatLogDate(item.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-slate-700 mt-1 leading-relaxed bg-slate-50/60 p-2 rounded-lg border border-slate-100">
                      {item.details}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
